import { useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import FolderBrowser from './components/navigation/FolderBrowser';
import useVersionCheck from './hooks/useVersionCheck';

import { Backdrop, CircularProgress } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import 'core-js';
import 'normalize.css';
import FindReplace from './FindReplace/FindReplace';
import LandingPage from './components/landing/LandingPage';
import VideoPlayerApp from './components/subtitles/SubtitleEditor';
import VideoPlayerAppV2 from './components/subtitles/SubtitleEditorV2';
import Login from './components/users/Login';
import { LoggingProvider } from './folders/LoggingProvider';
import './libs/contextmenu.css';
// Import admin components
import AdminDashboard from './components/admin/AdminDashboard';
import AssignmentDashboard from './components/admin/AssignmentDashboard';
import BugReportsAdmin from './components/admin/BugReportsAdmin';
import MongoFilesBrowser from './components/admin/MongoFilesBrowser';
import MongoVideosBrowser from './components/admin/MongoVideosBrowser';
import UserManagement from './components/admin/UserManagement';
import ProcessingStatusDashboard from './components/status/ProcessingStatusDashboard';
import { AssignmentProvider } from './contexts/AssignmentContext';
// Import video processing components
import VideoProcessingPanel from './components/status/VideoProcessingPanel';
import { VideoProcessingProvider } from './contexts/VideoProcessingContext';
// Import new components
import AppThemeProvider from './components/ThemeProvider';
import DomainMigration from './components/admin/DomainMigration';
import FolderPermissionManagement from './components/admin/FolderPermissionManagement';
import GroupManagement from './components/admin/GroupManagement';
import ImpactReportPage from './components/admin/ImpactReportPage';
import InstrumentationPage from './components/admin/InstrumentationPage';
import OpsToolDashboard from './components/admin/OpsToolDashboard';
import BugReportPage from './components/bugreport/BugReportPage';
import VersionUpdateDialog from './components/common/VersionUpdateDialog';
import EditingStatsPage from './components/editingstats/EditingStatsPage';
import GlossaryEditorPage from './components/glossary/GlossaryEditorPage';
import VideoEditingHelpPage from './components/help/VideoEditingHelpPage';
import IdmlBookEditor from './components/idml/IdmlBookEditor';
import MonitoringDashboard from './components/monitoring/MonitoringDashboard';
import ReelEditorPage from './components/reels/ReelEditorPage';
import ActiveSessionsPage from './components/status/ActiveSessionsPage';
import ReleaseNotesPage from './components/status/ReleaseNotesPage';
import SystemStatusPage from './components/status/SystemStatusPage';
import MultiColumnSubtitleViewer from './components/subtitles/multi-column/MultiColumnSubtitleViewer';
import TaskToolsPage from './components/tasks/TaskToolsPage';
import MyEmailSettingsPage from './components/tasks/MyEmailSettingsPage';
import TasksPage from './components/tasks/TasksPage';
import WorkFolderPage from './components/work/WorkFolderPage';
import PublicViewPage from './components/tasks/PublicViewPage';
import PublicEnglishFormPage from './components/tasks/PublicEnglishFormPage';
import TextEditor from './components/textfiles/TextEditor';
import TrashBinPage from './components/trash/TrashBinPage';
import VideoEditorPage from './components/videoeditor/VideoEditorPage';
import logCollectionService from './services/LogCollectionService';
import userServiceInstance from './services/UserService';
import AppConfig from './utils/AppConfig';
import { rememberLoginRedirect } from './utils/authUtils';

const defaultLang = 'en';
const FRONTEND_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

const VersionWatcher = ({ appVersion }) => {
    const location = useLocation();
    useVersionCheck(location.pathname, appVersion);
    return null;
};

// A signed-out visit to any other page (such as a task link from an email) goes to login,
// which returns there afterwards.
const LoginThenReturn = () => {
    const location = useLocation();
    rememberLoginRedirect(location.pathname + location.search);
    return <Navigate to="/login" replace />;
};

const AppContent = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const [authToken, setAuthToken] = useState(null);
    const [canNavigateToEdit, setCanNavigateToEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isGlossaryManager, setIsGlossaryManager] = useState(false);

    const getStoredAuthUser = () => {
        try {
            return JSON.parse(localStorage.getItem('authUser') || 'null');
        } catch (e) {
            return null;
        }
    };

    const updateAuthStateFromStorage = () => {
        const storedAuthToken = localStorage.getItem('authToken');
        const storedAuthUser = getStoredAuthUser();
        setAuthToken(storedAuthToken);
        setIsAdmin(storedAuthUser?.userType === 'admin');
        setIsGlossaryManager(
            storedAuthUser?.userType === 'glossary_manager' || storedAuthUser?.roles?.includes('glossary_manager')
        );
    };

    useEffect(() => {
        setIsLoading(true);
        const authToken = localStorage.getItem('authToken');
        const authUser = getStoredAuthUser();

        console.log('Auth Token:', authToken);
        console.log('Auth User:', authUser);
        console.log('Is admin:', authUser?.userType === 'admin');

        setAuthToken(authToken);
        // Check if user is admin based on stored user data
        setIsAdmin(authUser?.userType === 'admin');
        setIsGlossaryManager(
            authUser?.userType === 'glossary_manager' || authUser?.roles?.includes('glossary_manager')
        );

        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 2000);

        return () => {
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        const refreshAuthProfile = async () => {
            try {
                const token = localStorage.getItem('authToken');
                if (!token) {
                    return;
                }
                await userServiceInstance.refreshCurrentUserProfile();
                updateAuthStateFromStorage();
            } catch (e) {
                // Silent fail: stale permissions are safer than forcing logout on transient network errors.
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshAuthProfile();
            }
        };

        const onAuthUserUpdated = () => {
            updateAuthStateFromStorage();
        };

        refreshAuthProfile();
        window.addEventListener('focus', refreshAuthProfile);
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('auth-user-updated', onAuthUserUpdated);

        // Periodic heartbeat every 10 minutes so the user stays visible in "Currently Online"
        const heartbeatInterval = setInterval(refreshAuthProfile, 10 * 60 * 1000);

        return () => {
            window.removeEventListener('focus', refreshAuthProfile);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('auth-user-updated', onAuthUserUpdated);
            clearInterval(heartbeatInterval);
        };
    }, []);

    useEffect(() => {
        const applySeekDebugSettings = (cfg = AppConfig) => {
            const enabled = !!cfg.enableSeekDebug;
            const captureNetwork = !!cfg.enableSeekDebugNetworkCapture;

            // Backward-compatible key used by subtitle seek debug checks.
            localStorage.setItem('subtitle_seek_debug', enabled ? '1' : '0');

            logCollectionService.setSeekDebugMode(enabled, { captureNetwork });

            if (enabled) {
                logCollectionService.logTaggedEvent(
                    'seek_debug_runtime_config',
                    {
                        captureNetwork,
                    },
                    ['seek-debug', 'config'],
                );
            }
        };

        const onConfigUpdated = (event) => {
            applySeekDebugSettings(event?.detail || AppConfig);
        };

        applySeekDebugSettings(AppConfig);
        window.addEventListener('app-config-updated', onConfigUpdated);

        return () => {
            window.removeEventListener('app-config-updated', onConfigUpdated);
        };
    }, []);

    // Admin route guard component
    const AdminRoute = ({ children }) => {
        return isAdmin ? children : <Navigate to="/" />;
    };

    // Glossary route guard — accessible to admins and glossary managers
    const GlossaryRoute = ({ children }) => {
        return isAdmin || isGlossaryManager ? children : <Navigate to="/" />;
    };

    // Pass theme props to components that need them
    const renderComponent = (Component, props = {}) => {
        return <Component {...props} darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />;
    };

    if (isLoading) {
        return (
            <AssignmentProvider>
                <LoggingProvider>
                    <VideoProcessingProvider>
                        <AppBar
                            position="static"
                            style={{
                                backgroundColor: darkMode ? '#1E1E2D' : '#2C3D50',
                            }}
                        >
                            <Container maxWidth="xl">
                                <Toolbar disableGutters>
                                    <Typography
                                        variant="h6"
                                        noWrap
                                        component={'a'}
                                        target="_blank"
                                        href="https://www.srmd.org/"
                                        sx={{
                                            mr: 2,
                                            display: { xs: 'none', md: 'flex' },
                                            fontFamily: 'monospace',
                                            fontWeight: 700,
                                            letterSpacing: '.3rem',
                                            color: 'inherit',
                                            textDecoration: 'none',
                                        }}
                                    >
                                        <img
                                            style={{ width: '70px', height: '60px' }}
                                            className="SRMD-img"
                                            src={'/SRMD-Logo.png'}
                                            alt="SRMD_Logo"
                                        />
                                    </Typography>
                                </Toolbar>
                            </Container>
                        </AppBar>
                        <Backdrop sx={{ color: '#FFF', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
                            <CircularProgress color="inherit" />
                        </Backdrop>
                    </VideoProcessingProvider>
                </LoggingProvider>
            </AssignmentProvider>
        );
    } else {
        return (
            <AssignmentProvider>
                <LoggingProvider>
                    <VideoProcessingProvider>
                        <Router>
                            <VersionWatcher appVersion={FRONTEND_VERSION} />
                            {authToken === null ? (
                                <>
                                    <Routes>
                                        <Route path="/" element={renderComponent(LandingPage)} />
                                        <Route path="/" element={renderComponent(LandingPage)} />
                                        <Route path="/login" element={renderComponent(Login)} />
                                        {/* The link at the foot of every task email: sign in, then land on it */}
                                        <Route path="/me/email-settings" element={<LoginThenReturn />} />
                                        <Route path="*" element={<Navigate to="/" />} />
                                    </Routes>
                                </>
                            ) : (
                                <>
                                    <Routes>
                                        <Route
                                            path="/"
                                            element={renderComponent(FolderBrowser, { setCanNavigateToEdit })}
                                        />
                                        <Route
                                            path="/videos/edit"
                                            element={
                                                canNavigateToEdit ? (
                                                    <VideoPlayerApp defaultLang={defaultLang} />
                                                ) : (
                                                    <Navigate to="/" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="/videos/edit-v2"
                                            element={
                                                canNavigateToEdit ? (
                                                    <VideoPlayerAppV2 defaultLang={defaultLang} />
                                                ) : (
                                                    <Navigate to="/" />
                                                )
                                            }
                                        />
                                        <Route exact path="/find-replace" element={renderComponent(FindReplace)} />
                                        <Route path="/help" element={renderComponent(VideoEditingHelpPage)} />
                                        <Route path="/bug-report" element={renderComponent(BugReportPage)} />
                                        <Route path="/system-status" element={renderComponent(SystemStatusPage)} />
                                        <Route path="/release-notes" element={renderComponent(ReleaseNotesPage)} />
                                        <Route path="/status" element={<Navigate to="/system-status" replace />} />
                                        <Route path="/textfiles/edit/:id" element={renderComponent(TextEditor)} />
                                        <Route path="/idml/books/edit/:id" element={renderComponent(IdmlBookEditor)} />
                                        <Route path="/sessions/active" element={renderComponent(ActiveSessionsPage)} />
                                        <Route
                                            path="/status/processing-status"
                                            element={renderComponent(ProcessingStatusDashboard)}
                                        />
                                        <Route
                                            path="/multi-column-viewer/:videoId"
                                            element={renderComponent(MultiColumnSubtitleViewer)}
                                        />
                                        <Route path="/reels/create" element={renderComponent(ReelEditorPage)} />
                                        <Route path="/reels/create/:videoId" element={renderComponent(ReelEditorPage)} />
                                        <Route path="/videos/video-editor/:videoId" element={renderComponent(VideoEditorPage)} />
                                        <Route path="/glossary" element={<GlossaryRoute>{renderComponent(GlossaryEditorPage)}</GlossaryRoute>} />
                                        <Route path="/glossary/textfile/:textFileId" element={<GlossaryRoute>{renderComponent(GlossaryEditorPage)}</GlossaryRoute>} />
                                        <Route path="/glossary/:videoId" element={<GlossaryRoute>{renderComponent(GlossaryEditorPage)}</GlossaryRoute>} />
                                        <Route path="/editing-stats" element={renderComponent(EditingStatsPage)} />
                                        <Route path="/tasks" element={renderComponent(TasksPage, { setCanNavigateToEdit })} />
                                        {/* PMs and admins; the page itself checks. The old addresses land on their tab */}
                                        <Route path="/tasks/tools" element={renderComponent(TaskToolsPage)} />
                                        {/* Everyone; linked from the footer of every task email */}
                                        <Route path="/me/email-settings" element={renderComponent(MyEmailSettingsPage)} />
                                        <Route path="/tasks/english-srts" element={<Navigate to="/tasks/tools?tab=english" replace />} />
                                        <Route path="/work/video/:videoId" element={renderComponent(WorkFolderPage)} />
                                        <Route path="/work/post/:postId" element={renderComponent(WorkFolderPage)} />
                                        <Route path="/work/audio/:audioId" element={renderComponent(WorkFolderPage)} />
                                        <Route path="/admin/task-import" element={<Navigate to="/tasks/tools?tab=import" replace />} />
                                        {/* Admin Routes */}
                                        <Route
                                            path="/admin"
                                            element={<AdminRoute>{renderComponent(AdminDashboard)}</AdminRoute>}
                                        />
                                        <Route
                                            path="/admin/users"
                                            element={<AdminRoute>{renderComponent(UserManagement)}</AdminRoute>}
                                        />

                                        <Route path="/admin/mongo-files" element={renderComponent(MongoFilesBrowser)} />
                                        <Route
                                            path="/admin/mongo-videos"
                                            element={renderComponent(MongoVideosBrowser)}
                                        />
                                        <Route
                                            path="/admin/bug-reports"
                                            element={<AdminRoute>{renderComponent(BugReportsAdmin)}</AdminRoute>}
                                        />
                                        <Route
                                            path="/admin/assignments"
                                            element={<AdminRoute>{renderComponent(AssignmentDashboard)}</AdminRoute>}
                                        />
                                        <Route
                                            path="/trash"
                                            element={<AdminRoute>{renderComponent(TrashBinPage)}</AdminRoute>}
                                        />
                                        <Route
                                            path="/admin/groups"
                                            element={<AdminRoute>{renderComponent(GroupManagement)}</AdminRoute>}
                                        />
                                        <Route
                                            path="/admin/permissions"
                                            element={
                                                <AdminRoute>{renderComponent(FolderPermissionManagement)}</AdminRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/domain-migration"
                                            element={<AdminRoute> {renderComponent(DomainMigration)}</AdminRoute>}
                                        />
                                        <Route path="/admin/task-settings" element={<Navigate to="/tasks/tools" replace />} />
                                        <Route
                                            path="/admin/ops-tools"
                                            element={<AdminRoute>{renderComponent(OpsToolDashboard)} </AdminRoute>}
                                        />
                                        <Route path="/monitoring" element={renderComponent(MonitoringDashboard)} />
                                        <Route
                                            path="/instrumentation"
                                            element={<AdminRoute>{renderComponent(InstrumentationPage)}</AdminRoute>}
                                        />
                                        <Route
                                            path="/admin/impact-report"
                                            element={<AdminRoute>{renderComponent(ImpactReportPage)}</AdminRoute>}
                                        />

                                        <Route path="*" element={<Navigate to="/" />} />
                                    </Routes>
                                </>
                            )}
                        </Router>
                        <VersionUpdateDialog />
                        <VideoProcessingPanel />
                    </VideoProcessingProvider>
                </LoggingProvider>
            </AssignmentProvider>
        );
    }
};

const App = () => {
    // Public links (/f/:token shared folders, /v/:token saved task views, /r/:token English forms) render outside the logged-in shell and skip the login check
    if (typeof window !== 'undefined' && /^\/(f|v|r)\//.test(window.location.pathname)) {
        return (
            <AppThemeProvider>
                <Router>
                    <Routes>
                        <Route path="/f/:token" element={<WorkFolderPage />} />
                        <Route path="/v/:token" element={<PublicViewPage />} />
                        <Route path="/r/:token" element={<PublicEnglishFormPage />} />
                    </Routes>
                </Router>
            </AppThemeProvider>
        );
    }
    return (
        <AppThemeProvider>
            <AppContent />
        </AppThemeProvider>
    );
};

export default App;
